import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, ArrowLeft, Trash2, Plus, X, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const AdminMembers = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [memberForm, setMemberForm] = useState({ name: '', email: '', password: '', phone: '', address: '', role: 'customer' });
  const [editingMemberId, setEditingMemberId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch (error) {
      navigate('/admin/login');
      return;
    }
    
    loadMembers();
  }, [navigate]);

  const loadMembers = async () => {
    try {
      const response = await api.get('/admin/members');
      setMembers(response.data);
    } catch (error) {
      toast.error('Gagal memuat data member');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/admin/users', { ...memberForm, is_member: true });
      toast.success('Member berhasil ditambahkan');
      setIsAddModalOpen(false);
      setMemberForm({ name: '', email: '', password: '', phone: '', address: '', role: 'customer' });
      loadMembers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal menambahkan member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (member) => {
    setMemberForm({
      name: member.name,
      email: member.email,
      phone: member.phone,
      address: member.address,
      role: member.role || 'customer',
      password: ''
    });
    setEditingMemberId(member.id);
    setIsEditModalOpen(true);
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.put(`/admin/users/${editingMemberId}`, memberForm);
      toast.success('Data member berhasil diupdate');
      setIsEditModalOpen(false);
      setEditingMemberId(null);
      setMemberForm({ name: '', email: '', password: '', phone: '', address: '', role: 'customer' });
      loadMembers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal mengupdate member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (user) => {
    if (!window.confirm(`Yakin ingin menghapus ${user.name} dari member?`)) {
        return;
    }

    try {
      await api.put(`/admin/users/${user.id}/member`, null, {
        params: { is_member: false }
      });
      toast.success(`Member ${user.name} berhasil dihapus`);
      loadMembers();
    } catch (error) {
      toast.error('Gagal menghapus member');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/admin/dashboard" className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Admin Panel</span>
            </Link>
            <Link to="/admin/dashboard" className="text-zinc-400 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white">Daftar Member</h1>
            <button
              onClick={() => {
                setMemberForm({ name: '', email: '', password: '', phone: '', address: '', role: 'customer' });
                setIsAddModalOpen(true);
              }}
              className="flex items-center gap-2 bg-primary text-black px-4 py-2 rounded-md font-bold uppercase hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Member
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-500 text-lg">Belum ada member aktif</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-colors relative group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{member.name}</h3>
                      <span className="text-xs text-zinc-400">ID: {member.id.slice(0, 8)}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-primary mt-1" />
                      <div>
                        <div className="text-xs text-zinc-400">Email</div>
                        <div className="text-white text-sm">{member.email}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Phone className="w-4 h-4 text-primary mt-1" />
                      <div>
                        <div className="text-xs text-zinc-400">No. HP</div>
                        <div className="text-white text-sm">{member.phone}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-primary mt-1" />
                      <div>
                        <div className="text-xs text-zinc-400">Alamat</div>
                        <div className="text-white text-sm">{member.address}</div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-zinc-800 flex justify-end gap-4">
                       <button
                         onClick={() => openEditModal(member)}
                         className="flex items-center gap-2 text-blue-500 hover:text-blue-400 text-sm font-bold uppercase transition-colors"
                       >
                         <Edit2 className="w-4 h-4" />
                         Edit
                       </button>
                       <button
                         onClick={() => handleRemoveMember(member)}
                         className="flex items-center gap-2 text-red-500 hover:text-red-400 text-sm font-bold uppercase transition-colors"
                       >
                         <Trash2 className="w-4 h-4" />
                         Remove
                       </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {(isAddModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold italic uppercase text-white">
                {isAddModalOpen ? 'Add New Member' : 'Edit Member'}
              </h2>
              <button 
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditModalOpen(false);
                }} 
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={isAddModalOpen ? handleAddMember : handleEditMember} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Nama Lengkap</label>
                <input
                  type="text"
                  required
                  value={memberForm.name}
                  onChange={(e) => setMemberForm({...memberForm, name: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={memberForm.email}
                  onChange={(e) => setMemberForm({...memberForm, email: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                  placeholder="john@example.com"
                />
              </div>

              {isAddModalOpen && (
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1">Password</label>
                  <input
                    type="password"
                    required={isAddModalOpen}
                    value={memberForm.password}
                    onChange={(e) => setMemberForm({...memberForm, password: e.target.value})}
                    className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                    placeholder="••••••••"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">No. HP</label>
                <input
                  type="tel"
                  required
                  value={memberForm.phone}
                  onChange={(e) => setMemberForm({...memberForm, phone: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                  placeholder="081234567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Alamat</label>
                <textarea
                  required
                  value={memberForm.address}
                  onChange={(e) => setMemberForm({...memberForm, address: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary min-h-[80px]"
                  placeholder="Alamat lengkap..."
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-black font-bold uppercase py-3 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    isAddModalOpen ? <Plus className="w-5 h-5" /> : <Edit2 className="w-5 h-5" />
                  )}
                  {isSubmitting ? 'Menyimpan...' : (isAddModalOpen ? 'Add Member' : 'Save Changes')}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminMembers;
